import { Currency } from '../domain';

type Class<T> = { new(...args: any[]): T; };

type IdentityEntity<T> = { identity(): T };

interface HasId {
    uuid: string;
    toJson(): DomainJSON;
}

interface DomainJSON {
    uuid: string
}

interface HasBuilder<T> {
    builder() : Builder<T>
}

interface Builder<T> {
    build() : T,
    applyObj(object:T) : Builder<T>
    applyDiff(diff: DiffContent<T>) : Builder<T>
    getDiff() : DiffContent<T>
}

interface Finder<T> {
    findById(uuid: string ) : T | undefined;
}

interface Entity<T> extends HasBuilder<T>, HasId {

}

abstract class TrackingBuilder<T> implements Builder<T> {
    private _tracking: boolean = true;
    
    trackingOn() {
        this._tracking = true; 
        return this;
    }

    trackingOff() {
        this._tracking = false;
        return this;
    }

    isTracking() {return this._tracking;}

    abstract build(): T;
    abstract applyDiff(diff:DiffContent<T>): Builder<T>;
    abstract applyObj(object:T) : Builder<T>;
    abstract getDiff(): DiffContent<T>;
}


type Mutation = "ADD" | "EDIT" | "DELETE";

interface DiffContent<T> {
    uuid?:string,
}

interface Diff<T> {
    type: "" | Mutation
    content: DiffContent<T>    
}

interface CrDiff<T> {
    uuid: string,
    diffs: Diff<T>[]
}

interface Operation<T extends HasBuilder<T>> {
    apply(entity?: T): T 
}



class Add<T extends HasBuilder<T>> implements Operation<T> {
    private _json: DiffContent<T>;

    constructor(json: DiffContent<T>) {
        this._json = json;
    }

    apply(entity: T) : T {
        return entity
            .builder()
            .applyDiff(this._json)
            .build();
    }
}

class Edit<T extends HasBuilder<T>> implements Operation<T> {
    private _json: DiffContent<T>;

    constructor(json: DiffContent<T>) {
        this._json = json;
    }

    apply(entity : T) : T {
        const builder = entity.builder();
        const diffApplied = builder.applyDiff(this._json).build();       
        return diffApplied;
    }
}

class Delete<T extends HasBuilder<T>> implements Operation<T> {
    private _identityFunc: () => T;

    constructor(identityFunc:()=>T) {
        this._identityFunc = identityFunc;
    }

    apply() : T {
        return this._identityFunc();
    }
}

function applyOperations<T extends HasBuilder<T>, J extends Diff<T>>(jsons:J[], value:T | undefined, identityFunc:() => T) : T {
    let currentValue = value ? value : identityFunc();
    for (let i = 0; i < jsons.length; i++) {

        const json = jsons[i];

        let operation:Operation<T> | null = null;
        if (json.type === "ADD") {
            operation = new Add<T>(json.content)
        }
        else if (json.type === "EDIT") {
            operation = new Edit<T>(json.content)
        }
        else if (json.type === "DELETE") {
            operation = new Delete<T>(identityFunc);
        }
        if (!operation) {
            throw new Error("");
        }
        currentValue = operation.apply(currentValue);
    }
    return currentValue;
}


export {Class, IdentityEntity, HasId, Builder, Entity, Finder, 
    TrackingBuilder, HasBuilder, Diff, DiffContent, Mutation, CrDiff, Add, Edit, Delete, applyOperations}